import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';

export interface ChannelDestinationRow {
  publicId: string;
  workspaceName: string;
  workspacePublicId: string;
  type: 'whatsapp' | 'instagram' | 'facebook';
  channelStatus: 'connected' | 'disconnected' | 'archived';
  webhookUrl: string | null;
  whatsappWabaName: string | null;
  whatsappDisplayPhoneNumber: string | null;
  instagramProfileName: string | null;
  instagramUsername: string | null;
  facebookPageName: string | null;
  metaResourceId: string | null;
}

export interface WorkspaceOption {
  publicId: string;
  name: string;
}

export interface OrgDestination {
  webhookUrl: string | null;
  verifyToken?: string | null;
  hmacSecret?: string | null;
}

export interface OrgChannelsFilters {
  page: number;
  limit: number;
  q?: string;
  workspaceId?: string;
  type?: string;
  /** 'customer' (SaaS Channels) or 'team' (Workspaces Channels). */
  kind?: 'team' | 'customer';
  /** Server defaults to 'active' (connected only). */
  status?: 'active' | 'disconnected' | 'all';
}

export interface OrgChannelsResponse {
  rows: ChannelDestinationRow[];
  total: number;
}

interface OrgWorkspacesResponse {
  workspaces: WorkspaceOption[];
}

interface ApplyDestinationVars {
  scope: 'all' | 'selected';
  mode: 'org' | 'clear' | 'custom';
  /** Required when mode='custom': url + the handshake token it must echo. */
  url?: string;
  verifyToken?: string;
  channelPublicIds?: string[];
  kind?: 'team' | 'customer';
  filter?: { q?: string; type?: string; workspaceId?: string; status?: string };
}

interface MoveChannelVars {
  channelPublicId: string;
  targetWorkspacePublicId: string;
}

function channelsPath(orgId: string | undefined, filters: OrgChannelsFilters): string {
  const params = new URLSearchParams({
    page: String(filters.page),
    limit: String(filters.limit),
  });
  if (filters.q) params.set('q', filters.q);
  if (filters.workspaceId) params.set('workspaceId', filters.workspaceId);
  if (filters.type) params.set('type', filters.type);
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.status) params.set('status', filters.status);
  return `/organizations/${orgId}/channels-destinations?${params.toString()}`;
}

function invalidateOrgChannels(qc: ReturnType<typeof useQueryClient>, orgId: string | undefined) {
  if (!orgId) return undefined;
  return qc.invalidateQueries({ queryKey: qk.org(orgId, 'channels-destinations') });
}

export function useOrgChannels(
  orgId: string | undefined,
  filters: OrgChannelsFilters,
) {
  return useApiQuery<OrgChannelsResponse>(
    qk.org(orgId ?? '', 'channels-destinations', filters),
    channelsPath(orgId, filters),
    { enabled: !!orgId },
  );
}

export function useOrgDestination(orgId: string | undefined) {
  return useApiQuery<OrgDestination>(
    qk.org(orgId ?? '', 'destination'),
    `/organizations/${orgId}/destination`,
    { enabled: !!orgId },
  );
}

export function useOrgChannelWorkspaces(
  orgId: string | undefined,
  enabled: boolean,
  kind: 'team' | 'customer' = 'customer',
) {
  return useApiQuery<OrgWorkspacesResponse>(
    // One cache entry per kind slice (shared with the org pages' pickers).
    qk.org(orgId ?? '', 'workspaces', kind),
    `/organizations/${orgId}/workspaces?kind=${kind}`,
    { enabled: !!orgId && enabled },
  );
}

export function useSetCachedOrgDestinationUrl(orgId: string | undefined) {
  const qc = useQueryClient();
  return (hasUrl: boolean) => {
    if (!orgId) return;
    qc.setQueryData<OrgDestination>(qk.org(orgId, 'destination'), (prev) => ({
      ...prev,
      webhookUrl: hasUrl ? (prev?.webhookUrl ?? '') : null,
      ...(hasUrl ? {} : { verifyToken: null, hmacSecret: null }),
    }));
    void qc.invalidateQueries({ queryKey: qk.org(orgId, 'destination') });
  };
}

export function useMoveOrgChannel(orgId: string | undefined) {
  const qc = useQueryClient();
  return useApiMutation<void, MoveChannelVars>(
    ({ channelPublicId, targetWorkspacePublicId }) => ({
      path: `/channels/${channelPublicId}/move`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetWorkspacePublicId }),
      },
      fallback: 'Failed to move channel',
    }),
    {
      onSuccess: () =>
        Promise.all([
          invalidateOrgChannels(qc, orgId),
          orgId
            ? qc.invalidateQueries({ queryKey: qk.org(orgId, 'workspaces') })
            : undefined,
        ]),
    },
  );
}

export function useApplyOrgChannelDestinations(orgId: string | undefined) {
  const qc = useQueryClient();
  return useApiMutation<unknown, ApplyDestinationVars>(
    ({ scope, mode, url, verifyToken, channelPublicIds, kind, filter }) => {
      const body: Record<string, unknown> = { scope, mode };
      if (url) body.url = url;
      if (verifyToken) body.verifyToken = verifyToken;
      if (channelPublicIds) body.channelPublicIds = channelPublicIds;
      if (kind) body.kind = kind;
      if (filter) {
        if (filter.q) body.q = filter.q;
        if (filter.type) body.type = filter.type;
        if (filter.workspaceId) body.workspaceId = filter.workspaceId;
        if (filter.status) body.status = filter.status;
      }
      return {
        path: `/organizations/${orgId}/channels-destinations/apply`,
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        fallback: 'Failed to apply destination',
      };
    },
    {
      onSuccess: () => invalidateOrgChannels(qc, orgId),
    },
  );
}
